import { supabase } from '@/lib/supabase';

// Get or create session ID from localStorage
const getSessionId = (): string => {
  if (typeof window === 'undefined') return '';
  
  const storageKey = 'user_session_id';
  let sessionId = localStorage.getItem(storageKey);
  
  if (!sessionId) {
    // Generate unique session ID: timestamp + random string
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(storageKey, sessionId);
  }
  
  return sessionId;
};

// Session tracking for time spent
let sessionStartTime: number | null = null;
let pageLoadTime: number | null = null;
let maxScrollDepth = 0;
let scrollTrackingInitialized = false;
let visibilityTrackingInitialized = false;

interface TrackEventParams {
  eventName: string;
  metadata?: Record<string, any>;
}

export const eventTrackingService = {
  /**
   * Track anonymous user events to Supabase
   * GDPR-friendly: No personal data, only behavior patterns
   */
  async trackEvent({ eventName, metadata = {} }: TrackEventParams): Promise<void> {
    try {
      // Prepare event data
      const eventData = {
        event_name: eventName,
        timestamp: new Date().toISOString(),
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        session_id: getSessionId(),
        metadata: metadata
      };

      // Insert into Supabase (public_can_insert_events RLS policy allows this)
      const { error } = await supabase
        .from('events')
        .insert(eventData);

      if (error) {
        // Silent fail - don't interrupt user experience
        console.warn('Event tracking failed:', error.message);
      }
    } catch (error) {
      // Silent fail - tracking should never break the app
      console.warn('Event tracking exception:', error);
    }
  },

  /**
   * Initialize session tracking when page loads
   */
  initializeSessionTracking(): void {
    if (typeof window === 'undefined') return;
    
    // Track session start
    if (!sessionStartTime) {
      sessionStartTime = Date.now();
      pageLoadTime = Date.now();
      
      this.trackEvent({
        eventName: 'session_start',
        metadata: {
          referrer: document.referrer || 'direct',
          screen_width: window.innerWidth,
          screen_height: window.innerHeight
        }
      });
    }

    // Track page view
    this.trackEvent({
      eventName: 'page_view',
      metadata: {
        page_title: document.title,
        referrer: document.referrer || 'direct'
      }
    });

    // Initialize scroll tracking
    this.initializeScrollTracking();

    // Initialize visibility tracking (when user leaves page)
    this.initializeVisibilityTracking();
  },

  /**
   * Initialize scroll depth tracking
   */
  initializeScrollTracking(): void {
    if (typeof window === 'undefined' || scrollTrackingInitialized) return;
    
    scrollTrackingInitialized = true;
    let scrollTimeout: NodeJS.Timeout;

    const trackScrollDepth = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      
      const scrollPercent = Math.round((scrollTop / (docHeight - windowHeight)) * 100);
      
      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;
        
        // Track milestone scroll depths (25%, 50%, 75%, 100%)
        if (maxScrollDepth >= 25 && maxScrollDepth < 50) {
          this.trackEvent({
            eventName: 'scroll_depth',
            metadata: { depth_percent: 25 }
          });
        } else if (maxScrollDepth >= 50 && maxScrollDepth < 75) {
          this.trackEvent({
            eventName: 'scroll_depth',
            metadata: { depth_percent: 50 }
          });
        } else if (maxScrollDepth >= 75 && maxScrollDepth < 100) {
          this.trackEvent({
            eventName: 'scroll_depth',
            metadata: { depth_percent: 75 }
          });
        } else if (maxScrollDepth >= 100) {
          this.trackEvent({
            eventName: 'scroll_depth',
            metadata: { depth_percent: 100 }
          });
        }
      }
    };

    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(trackScrollDepth, 150);
    }, { passive: true });
  },

  /**
   * Initialize visibility tracking to capture session end
   */
  initializeVisibilityTracking(): void {
    if (typeof window === 'undefined' || visibilityTrackingInitialized) return;
    
    visibilityTrackingInitialized = true;

    // Track when user leaves page (session end)
    const trackSessionEnd = () => {
      if (sessionStartTime && pageLoadTime) {
        const sessionDuration = Math.round((Date.now() - sessionStartTime) / 1000); // seconds
        const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000); // seconds
        
        this.trackEvent({
          eventName: 'session_end',
          metadata: {
            session_duration_seconds: sessionDuration,
            time_on_page_seconds: timeOnPage,
            max_scroll_depth_percent: maxScrollDepth
          }
        });
      }
    };

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        trackSessionEnd();
      }
    });

    // Track when user navigates away
    window.addEventListener('beforeunload', trackSessionEnd);

    // Track when user closes tab
    window.addEventListener('pagehide', trackSessionEnd);
  },

  /**
   * Track calculator button click
   */
  trackCalculatorClick(weight: number, purity: number, estimatedValue: number): void {
    this.trackEvent({
      eventName: 'calculator_button_click',
      metadata: {
        weight,
        purity,
        estimated_value: estimatedValue,
        timestamp_local: new Date().toLocaleString('nb-NO')
      }
    });
  },

  /**
   * Track when user starts typing in lead form
   */
  trackLeadFormTypingStart(fieldName: string): void {
    this.trackEvent({
      eventName: 'lead_form_typing_start',
      metadata: {
        field_name: fieldName,
        timestamp_local: new Date().toLocaleString('nb-NO')
      }
    });
  },

  /**
   * Track lead form submission
   */
  trackLeadFormSubmission(weight: number, purity: number, estimatedValue: number, success: boolean): void {
    this.trackEvent({
      eventName: 'lead_form_submission',
      metadata: {
        weight,
        purity,
        estimated_value: estimatedValue,
        submission_success: success,
        timestamp_local: new Date().toLocaleString('nb-NO')
      }
    });
  },

  /**
   * Track generic button clicks
   */
  trackButtonClick(buttonName: string, buttonLocation: string): void {
    this.trackEvent({
      eventName: 'button_click',
      metadata: {
        button_name: buttonName,
        button_location: buttonLocation
      }
    });
  }
};